import React, { useState, useEffect, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { formatTime } from './utils/formatTime';



export default function StopWatch({ result, onUpdate }) {
    const [isRunning, setIsRunning] = useState(false); // Track if the stopwatch is running
    const [time, setTime] = useState(result); // Initialize with `result`
    const startTimeRef = useRef(null); // Reference to store the start time
    const elapsedTimeRef = useRef(time); // Reference to store the elapsed time
    const [isEditing, setIsEditing] = useState(false); // Track if the input is being edited
    const [inputValue, setInputValue] = useState('00:00.00'); // Editable time value


    // Effect to start/stop the stopwatch based on `isRunning` state
    useEffect(() => {
        let animationFrameId;

        const updateTimer = () => {
            if (isRunning) {
                const now = Date.now();
                const elapsed = Math.floor((now - startTimeRef.current) / 10);
                setTime(elapsedTimeRef.current + elapsed);
                animationFrameId = requestAnimationFrame(updateTimer);
            }
        };

        if (isRunning) {
            startTimeRef.current = Date.now();
            animationFrameId = requestAnimationFrame(updateTimer);
        } else {
            if (elapsedTimeRef.current !== null) {
                elapsedTimeRef.current = time;
            }
        }

        return () => cancelAnimationFrame(animationFrameId);
    }, [isRunning]); // Dependency array includes `isRunning`


    // Function to toggle the stopwatch
    const toggleStopwatch = () => {
        setIsRunning(prev => !prev); // Toggle state based on the previous state
        if (isRunning) {
            elapsedTimeRef.current = time;
            setInputValue(formatTime(time)); // Ensure the input value is updated with the current time

        }
        onUpdate(time);

    };

    // Reset the time to zero
    const handleResetClick = () => {
        if (!isRunning) {
            setTime(0);
            elapsedTimeRef.current = 0;

            onUpdate(0);
            setInputValue('00:00.00'); // Reset inputValue if not running
        }
    };

    // Toggle editing mode
    const handleEditClick = () => {
        if (!isRunning) {
            setIsEditing(prev => !prev); // Toggle editing mode if not running
            if (!isEditing) {
                setInputValue(formatTime(time)); // Set current time in inputValue for editing
            }
        }
    };


    // Handle input change
    const handleInputChange = (event) => {
        setInputValue(event.target.value);
    };

    // Handle applying the new time
    const handleApplyClick = () => {
        const regex = /^(\d{2}):(\d{2})\.(\d{2})$/;
        const match = inputValue.match(regex);
        if (match) {
            const minutes = parseInt(match[1], 10);
            const seconds = parseInt(match[2], 10);
            const hundredths = parseInt(match[3], 10);
            const totalHundredths = (minutes * 60 * 100) + (seconds * 100) + hundredths;
            setTime(totalHundredths);
            setIsEditing(false); // Exit editing mode
            onUpdate(totalHundredths);
        } else {
            // Handle invalid time format
            alert('Invalid time format. Please use MM:SS.hh');
        }
    };

    return (
        <div className="p-inputgroup md:w-30rem">
            <span className="p-inputgroup-addon" onClick={toggleStopwatch}>
                <i
                    className={`pi ${isRunning ? 'pi-pause' : 'pi-clock'}`}

                ></i>
            </span>
            {isEditing ? (
                <>
                    <InputText
                        value={inputValue}
                        onChange={handleInputChange}
                        placeholder="MM:SS.hh"
                        style={{ fontFamily: 'monospace' }} />
                    <span className="p-inputgroup-addon" onClick={handleApplyClick}>
                        <i
                            className="pi pi-check ml-2"

                        ></i>
                    </span>
                </>
            ) : (
                <InputText placeholder={formatTime(time)} readOnly style={{ fontFamily: 'monospace' }} />
            )}
            <span className="p-inputgroup-addon" onClick={handleResetClick}>
                <i
                    className="pi pi-refresh"

                ></i>
            </span>
            <span className="p-inputgroup-addon" onClick={handleEditClick}>
                <i
                    className={`pi ${isEditing ? 'pi-times' : 'pi-pen-to-square'}`}

                ></i>
            </span>
        </div>
    );
}
